/**
 * TemplateGuideOverlay — calibration-upload-guidance
 *
 * Coach-facing Adaptive Guidance Overlay for the Template Details page's
 * Calibration Takes panel — the "detailed" tier of the two-tier calibration
 * example-video guidance (the "short" tier is condensed inline copy on the
 * upload panel itself; see CalibrationTakesPanel.tsx / CalibrationTakeUploader.tsx).
 *
 * Mirrors StudioGuideOverlay's composition (GuideDrawerLayout +
 * GuideCardContent + localStorage dismissal persistence + useAnchorSpy) but
 * with a single-level content-resolution chain: this surface has no
 * focusedField / selectedTool / activePanel / activePhase concept, only
 * `activeSection`, driven by useAnchorSpy over `data-guide-section` markers
 * in CalibrationTakesPanel / CalibrationTakeUploader / CalibrationTakeList.
 *
 * Content reactivity (priority order):
 *   1. activeSection — scroll-spy / focus signal for the visible calibration
 *      region (calibration-good / -bad / -fail / -reason / -results)
 *   2. null           — 'calibration-overview' fallback
 */

import { useCallback } from 'react';
import { useAdaptiveGuide } from '@/contexts/AdaptiveGuideContext';
import { resolveTemplateGuideContent } from '@/data/templateGuideContent';
import { resolveGuideBody } from '@/data/guideContentTypes';
import { GUIDE_DISMISSED_TEMPLATE_KEY } from '@/utils/constants';
import { GuideCardContent } from './GuideCardContent';
import { GuideDrawerLayout } from './GuideDrawerLayout';
import { useAnchorSpy } from '@/hooks/useAnchorSpy';

export function TemplateGuideOverlay() {
  const { isGuideOpen, setGuideOpen, activeSection, report } = useAdaptiveGuide();

  // Drive activeSection from data-guide-section elements in the calibration
  // takes panel / uploader / list.
  useAnchorSpy(report, 'data-guide-section');

  const entry = resolveTemplateGuideContent(activeSection);
  const body = resolveGuideBody(entry, {});
  const contextHint = activeSection
    ? 'Showing guidance for the visible section.'
    : 'Scroll to or pick a take type below to see contextual tips.';

  const handleDismiss = useCallback(() => {
    setGuideOpen(false);
    try {
      localStorage.setItem(GUIDE_DISMISSED_TEMPLATE_KEY, 'true');
    } catch {
      // localStorage unavailable — non-fatal.
    }
  }, [setGuideOpen]);

  if (!isGuideOpen) return null;

  return (
    <GuideDrawerLayout
      isOpen={isGuideOpen}
      onDismiss={handleDismiss}
      heading={entry.heading}
      contextHint={contextHint}
    >
      <GuideCardContent body={body} />
    </GuideDrawerLayout>
  );
}

export default TemplateGuideOverlay;
